import { InlineSkeleton } from '@/components/loading/Skeleton'
import { cn } from '@/lib/utils';
import { Collapsible } from '@/components/play';
import type { ToolUse, ToolResult } from '@/hooks/useDirector';
import { CheckCircle2, XCircle, Wrench, FileEdit, Eye, Sparkles, Code, Layout } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────

interface ToolExecutionCardProps {
  toolUse: ToolUse;
  toolResult?: ToolResult;
  className?: string;
}

// ─── Tool Icon Mapping ────────────────────────────────────────────

const TOOL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Read: Eye,
  Write: FileEdit,
  Edit: FileEdit,
  page_update: Layout,
  generate_preview: Eye,
  publish_page: Sparkles,
  default: Wrench,
};

// ─── Helper Functions ─────────────────────────────────────────────

function getToolDisplayName(name: string): string {
  const displayNames: Record<string, string> = {
    Read: 'Reading file',
    Write: 'Writing file',
    Edit: 'Editing file',
    page_update: 'Updating page',
    generate_preview: 'Generating preview',
    publish_page: 'Publishing page',
    get_page_config: 'Loading config',
    rollback_page: 'Rolling back',
    list_page_versions: 'Loading versions',
    overlay_update: 'Updating overlay',
    leaderboard_update: 'Updating leaderboard',
    viewer_notification: 'Sending notification',
  };
  return displayNames[name] || name.replace(/_/g, ' ');
}

function formatParams(params: Record<string, unknown>): string {
  // Show relevant params without overwhelming detail
  const entries = Object.entries(params);
  if (entries.length === 0) return '';

  // Prioritize common params
  const priorityKeys = ['file_path', 'path', 'url', 'component', 'name'];
  for (const key of priorityKeys) {
    if (params[key]) {
      const value = params[key];
      if (typeof value === 'string') {
        // Truncate long paths/values
        const display = value.length > 40 ? '…' + value.slice(-39) : value;
        return display;
      }
    }
  }

  // Fallback to first string param
  for (const [, value] of entries) {
    if (typeof value === 'string' && value.length > 0) {
      return value.length > 40 ? value.slice(0, 39) + '…' : value;
    }
  }

  return '';
}

// ─── Component ────────────────────────────────────────────────────

export function ToolExecutionCard({ toolUse, toolResult, className }: ToolExecutionCardProps) {
  const isExecuting = !toolResult;
  const isSuccess = toolResult?.success !== false;

  const IconComponent = TOOL_ICONS[toolUse.name] || TOOL_ICONS.default;

  const paramDisplay = formatParams(toolUse.params);

  return (
    <div
      className={cn(
        'group relative rounded-lg bg-neutral-1 px-3 py-2.5 shadow-[var(--play-edge-control)]',
        'transition-all',
        toolResult && isSuccess && 'bg-success-transparent shadow-none',
        toolResult && !isSuccess && 'bg-error-transparent shadow-none',
        className
      )}
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div
          className={cn(
            'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center',
            'bg-neutral-3 transition-colors'
          )}
        >
          {isExecuting ? (
            <InlineSkeleton label={getToolDisplayName(toolUse.name)} className="h-4 w-4 text-neutral-10" />
          ) : (
            <IconComponent className="h-4 w-4 text-neutral-10" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-neutral-10">
              {getToolDisplayName(toolUse.name)}
            </span>
          </div>
          {paramDisplay && (
            <p className="text-xs text-neutral-7 truncate mt-xxs font-mono">
              {paramDisplay}
            </p>
          )}
        </div>

        {/* Status Icon */}
        <div className="flex-shrink-0">
          {toolResult && (
            isSuccess ? (
              <CheckCircle2 role="img" aria-label="Done" className="w-4 h-4 text-success" />
            ) : (
              <XCircle role="img" aria-label="Failed" className="w-4 h-4 text-error" />
            )
          )}
        </div>
      </div>

      {/* The result behind a Play collapsible, so touch and keyboard reach it as well as a pointer. */}
      {toolResult && toolResult.result && (
        <Collapsible className="mt-xxs" trigger="Result">
          <p className="text-xs text-neutral-7 line-clamp-3 font-mono">
            {toolResult.result.slice(0, 200)}
            {toolResult.result.length > 200 && '…'}
          </p>
        </Collapsible>
      )}
    </div>
  );
}

export default ToolExecutionCard;
